import { useMemo, useState } from "react";
import { PageContainer } from "@volca/ui/components/ui/page-components";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { AppAside } from "@/app-shell";
import { CELL_FIGURE_QUESTION } from "@/payroll/ai-script/ai-script-explanation-chat/selection-context";
import { AiScriptExplanationChatSidebar } from "@/payroll/ai-script/ai-script-explanation-chat-sidebar";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import { WORKBOOK_FRAME } from "@/profitability/constants";
import { ReportCurrencyProvider } from "@/profitability/currency";
import {
  InteractiveReport,
  RowDetail,
  RowDetailProvider,
  useRowDetail,
} from "@/profitability/interactive-report";
import { chatSurface } from "@/profitability/job-costing-run/chat-surface";
import { RunChecksStrip } from "@/profitability/job-costing-run/run-checks-strip";
import { RunContent } from "@/profitability/job-costing-run/run-content";
import { RunHeader } from "@/profitability/job-costing-run/run-header";
import { useReportOverview } from "@/profitability/overview/query";
import { useReportOutput } from "@/profitability/query";
import { RecordPanel } from "@/profitability/records";
import {
  findRecord,
  objectsOf,
  recordId,
  recordLabel,
  type RecordRef,
} from "@/profitability/records/objects";
import { ReviewScopeProvider } from "@/profitability/records/objects-context";
import { RecordSheet } from "@/profitability/records/record-sheet";
import { ReportWorkbook } from "@/profitability/report-workbook";
import { useFindingsReview } from "@/profitability/review/query";
import { ReviewSpecProvider } from "@/profitability/review/review-spec";
import { DraftSampleBanner } from "@/profitability/run-detail/draft-sample-banner";
import { MatchedColumnsBanner } from "@/profitability/run-detail/matched-columns-banner";
import type { EnrichedProfitabilityRun } from "@/profitability/types";
import { reportChatPath } from "@/profitability/utils";
import { runDates } from "@/profitability/utils";
const Report = ({
  businessId,
  run,
}: {
  businessId: string;
  run: EnrichedProfitabilityRun;
}) => {
  const rowDetail = useRowDetail()!;
  const [selectionContext, setSelectionContext] =
    useState<AiScriptChatSelectionContext | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { workbookReady } = run.display;

  const openRow = rowDetail.open;
  const output = useReportOutput(
    businessId,
    run.interactive_available ? run.id : "",
  );
  const objects = useMemo(() => objectsOf(output.data), [output.data]);
  // The API's reading of the review, defaults filled, when the run belongs
  // to a template. A draft's sample has no template overview, so its page
  // reads the neutral spec: the app never fills a declaration in itself.
  const overview = useReportOverview(
    businessId,
    run.report?.id ?? "",
    run.id,
    null,
    !run.draft_sample,
  );
  const spec = overview.data?.review ?? null;
  const findingsReview = useFindingsReview(businessId, run);
  const selected: RecordRef | null = openRow
    ? {
        dataset: openRow.dataset.id,
        recordId: recordId(openRow.dataset, openRow.row),
      }
    : null;
  const surface = useMemo(
    () =>
      chatSurface({
        objects,
        figures: run.summary.map((figure) => figure.label),
        spec,
      }),
    [objects, run.summary, spec],
  );
  const openObject =
    !!openRow &&
    objects.some((object) => object.dataset.id === openRow.dataset.id);
  const handleChatOpenChange = (open: boolean) => {
    setIsChatOpen(open);
    if (open) rowDetail.closeRow();
    if (!open) {
      setSelectionContext(null);
      setPendingQuestion(null);
    }
  };
  const openRef = (ref: RecordRef) => {
    const target = objects.find((item) => item.dataset.id === ref.dataset);
    const row = target && findRecord(target.dataset, ref.recordId);
    if (target && row) rowDetail.openRow({ row, dataset: target.dataset });
  };
  const handleSidebarOpenChange = (open: boolean) => {
    if (!open) rowDetail.closeRow();
  };

  const handleAskAiAboutCell = (context: AiScriptChatSelectionContext) => {
    setSelectionContext(context);
    setPendingQuestion(CELL_FIGURE_QUESTION);
    handleChatOpenChange(true);
  };

  return (
    <ReportCurrencyProvider
      currency={output.data?.currency ?? overview.data?.currency}
    >
      <ReviewSpecProvider spec={spec}>
        <SimpleSidebarLayout
          open={!!openRow && !openObject}
          onOpenChange={handleSidebarOpenChange}
        >
          {/* One scroller for the whole report, the header and the tabs included,
          so they scroll away rather than pinning the content into a box of
          its own. The page frame sits inside it, so its bar rides the edge. */}
          <SimpleSidebarContent>
            <ReviewScopeProvider
              scope={
                run.report
                  ? {
                      businessId,
                      reportId: run.report.id,
                      runId: run.id,
                      ...findingsReview,
                    }
                  : null
              }
            >
              <PageContainer>
                <RunHeader
                  businessId={businessId}
                  run={run}
                  onOpenChat={() => handleChatOpenChange(true)}
                />
                {run.draft_sample && (
                  <DraftSampleBanner businessId={businessId} run={run} />
                )}
                <MatchedColumnsBanner run={run} />
                <RunChecksStrip run={run} output={output.data} />
                <RunContent businessId={businessId} run={run}>
                  {run.interactive_available ? (
                    // A report that declared datasets is read as a dashboard, with the
                    // grid of the same figures as its last tab. It draws its own panes,
                    // so the app must not put another one around the whole thing.
                    <InteractiveReport
                      businessId={businessId}
                      runId={run.id}
                      dates={runDates(run)}
                      run={run}
                      sheet={run.workbook_available}
                      onAskAiAboutCell={handleAskAiAboutCell}
                    />
                  ) : (
                    // A report written before any of this declared nothing, so the
                    // workbook is all it has, and the app frames it.
                    <Stack
                      gap={0}
                      align="stretch"
                      className={cn("w-full", {
                        [WORKBOOK_FRAME]: workbookReady,
                      })}
                    >
                      <ReportWorkbook
                        businessId={businessId}
                        runId={run.id}
                        onAskAiAboutCell={handleAskAiAboutCell}
                      />
                    </Stack>
                  )}
                </RunContent>
              </PageContainer>
            </ReviewScopeProvider>
          </SimpleSidebarContent>

          <SimpleSidebar>
            {openRow && !openObject && (
              <RowDetail open={openRow} onClose={() => rowDetail.closeRow()} />
            )}
          </SimpleSidebar>
          {/* A record floats over the page in a sheet; the app's panel is the chat's. */}
          <RecordSheet
            open={!!openRow && !!openObject && !!run.report}
            onClose={() => rowDetail.closeRow()}
            label={selected ? recordLabel(objects, selected) : "Record"}
          >
            {selected && openObject && run.report && (
              <RecordPanel
                key={JSON.stringify([selected.dataset, selected.recordId])}
                businessId={businessId}
                reportId={run.report.id}
                objects={objects}
                selected={selected}
                onOpenRecord={openRef}
                onClose={() => rowDetail.closeRow()}
              />
            )}
          </RecordSheet>
          <AppAside open={isChatOpen && !openRow} label="Ask Volca">
            <AiScriptExplanationChatSidebar
              businessId={businessId}
              runId={run.id}
              buildChatPath={reportChatPath}
              selectionContext={selectionContext}
              onClearSelectionContext={() => setSelectionContext(null)}
              pendingQuestion={pendingQuestion}
              sendPendingQuestion
              onQuestionPrepared={() => setPendingQuestion(null)}
              title={surface.title}
              subtitle={surface.subtitle}
              emptyTitle={surface.emptyTitle}
              emptyDescription={surface.emptyDescription}
              composerPlaceholder={surface.composerPlaceholder}
              suggestedPrompts={surface.suggestedPrompts}
              acceptsImages
              onClose={() => handleChatOpenChange(false)}
            />
          </AppAside>
        </SimpleSidebarLayout>
      </ReviewSpecProvider>
    </ReportCurrencyProvider>
  );
};

export const LoadedReport = (props: {
  businessId: string;
  run: EnrichedProfitabilityRun;
}) => (
  <RowDetailProvider>
    <Report {...props} />
  </RowDetailProvider>
);
